import React from "react";
import Image from "components/image";
import Collapsible from "react-collapsible";
import Link from "helpers/link";
import { ReactComponent as ArrowDown } from "assets/svgs/arrow-down-bold.svg";
import { ReactComponent as Instagram } from "assets/svgs/instagram.svg";
import { ReactComponent as Facebook } from "assets/svgs/fb.svg";
import { ReactComponent as Youtube } from "assets/svgs/youtube.svg";
import { ReactComponent as Mail } from "assets/svgs/email.svg";
import { ReactComponent as Linkedin } from "assets/svgs/linkedin.svg";
import { ReactComponent as TikTok } from "assets/svgs/tiktok.svg";
import LogOutUser from "components/account/log-out";
import { useGlobalContext } from "context/global";
import { getRgba } from "helpers/get-rgba";
import LanguagePicker from "components/language-picker";
import clsx from "clsx/lite";
import useToggle from "hooks/useToggle";
import { useDispatch, useSelector } from "react-redux";
import { TOGGLE_SAVE_POPUP } from "utils/action-types";

const Icon = {
	facebook: <Facebook />,
	instagram: <Instagram />,
	youtube: <Youtube />,
	email: <Mail />,
	linkedin: <Linkedin />,
	tiktok: <TikTok />,
};

export const ImageLinks = ({ data }) => (
	<div className="group">
		<Link className="" to={data.url}>
			{data.title}
		</Link>
		<div className="absolute px-[1.8em] left-0 hidden group-hover:flex flex-wrap bg-white z-10">
			{data.imageLinkData.map((link) => (
				<div className="p-3 w-1/6" key={link._key}>
					<Link to={link.url ?? ""} className="">
						<Image image={link.linkImage} />
						<span className="flex justify-center py-5">{link.title}</span>
					</Link>
				</div>
			))}
		</div>
	</div>
);


const CustomButtonLink = ({ toggleMenu, isOpen, link, className = "" }) => {
	const { pendingChanges } = useSelector((state) => state.account.subscription);
	const dispatch = useDispatch();

	const handleClick = (event) => {
		if (pendingChanges) {
			event.preventDefault();
			dispatch({ type: TOGGLE_SAVE_POPUP, payload: { show: true ,redirectPath: link?.url } });
			return;
		}

		if (isOpen) {
			toggleMenu();
			// Refresh the page if the URL is /b2b to ensure Klaviyo forms render correctly
			if (link?.url === "/b2b") {
				event.preventDefault(); 
				window.location.href = link?.url;
			}
		}
	};

	return (
		<Link to={link?.url} className={className} onClick={handleClick}>
			{link?.title}
		</Link>
	);
};

export const GroupLinks = ({ data, style = "", isOpen, toggleMenu, isHeaderLink }) => {
	const [isMenuOpen, toggle] = useToggle();
	return (
		<>
			{data?.open ?
				<div className={`py-5 font-body mobile-menu-border-bottom text-body ${style}`}>
					<div className="pb-2.5">
						<Link to={data.url}>{data.title}</Link>
					</div>
					<ul className="ml-0 pt-2 list-none mobile-menu-border-top">
						{data.groupLinkData.map((link) => (
							link._type === "logOutLink" ?
								<LogOutUser key={link?._key} textColor={getRgba(link.color)} className="capitalize text-body" /> :
								<li key={link._key} className={`${isHeaderLink ? "py-2 last:pb-0" : "py-4 last:pb-2 px-6"}`}>
									<CustomButtonLink toggleMenu={toggleMenu} isOpen={isOpen} link={link} className="capitalize text-body" />
								</li>
						))}
					</ul>
				</div> :
				<Collapsible
					transitionTime="200"
					triggerTagName="div"
					onOpening={toggle}
					onClosing={toggle}
					triggerOpenedClassName="font-boldParagraph"
					triggerElementProps={{ id: data._key }}
					contentElementId={data._key + "_content"}
					trigger={[data.title, <ArrowDown key={data._key} className={clsx("inline float-right transition-all duration-600", isMenuOpen && "rotate-180")} />]}
					className={`!py-5 !pr-5 ${isHeaderLink ? "!pl-0" : "!pl-5"} text-body mobile-menu-border-bottom font-body ${style}`}
					openedClassName={`!py-5 !pr-5 ${isHeaderLink ? "!pl-0" : "!pl-5"} text-body mobile-menu-border-bottom font-body`}
				>
					<ul className="ml-0 pt-2 list-none">
						{data.groupLinkData.map((link) => (
							link._type === "logOutLink" ?
								<LogOutUser key={link?._key} textColor={getRgba(link.color)} className="capitalize text-body" /> :
								<li key={link._key} className={`${isHeaderLink ? "py-2 last:pb-0" : "py-4 last:pb-2 px-6"}`}>
									<CustomButtonLink toggleMenu={toggleMenu} isOpen={isOpen} link={link} className="capitalize text-body" />
								</li>
						))}
					</ul>
				</Collapsible>

			}
		</>
	);
};

export const DirectLink = ({ data, style = "", isOpen, toggleMenu }) => (
	<div title={data?.title}
		style={{ color: getRgba(data.color) }} className={`py-5 font-body mobile-menu-border-bottom text-body ${style}`}>
		<CustomButtonLink toggleMenu={toggleMenu} isOpen={isOpen} link={data} />
	</div>
);

export const SocialMedia = ({ list = ["instagram", "facebook", "youtube", "email"] }) => {
	const { social } = useGlobalContext();
	return (
		social && list.map((item) => (
			<Link key={item} to={social[item]} aria-label={item}>
				{Icon[item]}
			</Link>
		))
	);
};

export const IconLink = ({ link, path, key, className }) => (
	<div key={key || link?.key} className="flex justify-between items-center">
		<Link
			to={link?.url}
			className={clsx("py-5 text-body flex hover:underline", className)}
		>
			<img src={link?.icon} alt={link?.title} className="mr-3 lg:w-5"/>
			{link.title}
		</Link>
		{link?.langSelector && <div className="pr-5 lg:self-end lg:pb-0.5"><LanguagePicker path={path} /></div>}
	</div>
);
